import axios from "axios";

/**
 * Encode an AudioBuffer to a WAV Blob (browser-native, no dependencies)
 */
function audioBufferToWavBlob(audioBuffer: AudioBuffer): Blob {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const format = 1; // PCM
  const bitDepth = 16;

  // Interleave channels
  const length = audioBuffer.length * numChannels;
  const samples = new Int16Array(length);

  for (let channel = 0; channel < numChannels; channel++) {
    const channelData = audioBuffer.getChannelData(channel);
    for (let i = 0; i < audioBuffer.length; i++) {
      // Clamp and convert float32 to int16
      const s = Math.max(-1, Math.min(1, channelData[i]));
      samples[i * numChannels + channel] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
  }

  const byteLength = samples.length * 2;
  const buffer = new ArrayBuffer(44 + byteLength);
  const view = new DataView(buffer);

  // WAV header
  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + byteLength, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, format, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * (bitDepth / 8), true);
  view.setUint16(32, numChannels * (bitDepth / 8), true);
  view.setUint16(34, bitDepth, true);
  writeString(36, "data");
  view.setUint32(40, byteLength, true);

  // Write PCM samples
  const offset = 44;
  for (let i = 0; i < samples.length; i++) {
    view.setInt16(offset + i * 2, samples[i], true);
  }

  return new Blob([buffer], { type: "audio/wav" });
}

/**
 * Extract audio using AudioContext.decodeAudioData (fast, no playback needed)
 */
async function extractAudioWithAudioContext(
  arrayBuffer: ArrayBuffer,
  onProgress?: (message: string) => void,
): Promise<Blob> {
  onProgress?.("Decoding audio");

  const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
  if (!AudioCtx) {
    throw new Error("AudioContext not supported");
  }

  const audioContext = new AudioCtx();

  try {
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    // console.log(
    //   "AudioContext decoded:",
    //   audioBuffer.duration,
    //   "seconds,",
    //   audioBuffer.numberOfChannels,
    //   "channels",
    // );

    onProgress?.("Encoding audio");
    const wavBlob = audioBufferToWavBlob(audioBuffer);
    // console.log("WAV blob created:", wavBlob.size, "bytes");

    return wavBlob;
  } finally {
    await audioContext.close();
  }
}

/**
 * Extract audio using captureStream (fallback - requires real-time playback)
 */
function extractAudioWithCaptureStream(
  videoURL: string,
  shouldRevokeURL: boolean,
  onProgress?: (message: string) => void,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    if (!window.MediaRecorder) {
      reject(new Error("MediaRecorder not supported in this browser."));
      return;
    }

    onProgress?.("Loading video for audio capture");

    const video = document.createElement("video");
    video.setAttribute("playsinline", "true");
    video.setAttribute("webkit-playsinline", "true");
    // Only set crossorigin for http URLs, not blob URLs
    if (videoURL.startsWith("http")) {
      video.setAttribute("crossorigin", "anonymous");
    }
    video.muted = true;
    video.preload = "auto";

    video.src = videoURL;
    video.load();

    const cleanup = () => {
      if (shouldRevokeURL) {
        URL.revokeObjectURL(videoURL);
      }
      video.pause();
      video.removeAttribute("src");
      video.load();
    };

    video.onloadedmetadata = async () => {
      try {
        if (
          !video.duration ||
          isNaN(video.duration) ||
          video.duration === Infinity
        ) {
          throw new Error("Video has invalid duration.");
        }

        onProgress?.("Extracting audio from video");

        // Play muted to satisfy autoplay policy
        await video.play();
        await new Promise((r) => setTimeout(r, 300));

        // Get stream
        const stream = (video as any).captureStream
          ? (video as any).captureStream()
          : (video as any).mozCaptureStream?.();

        if (!stream) {
          throw new Error("captureStream not supported in this browser.");
        }

        // After getting stream, unmute so audio tracks are active in the stream
        // Keep volume at 0 so user doesn't hear playback
        video.muted = false;
        video.volume = 0;

        const audioTracks = stream.getAudioTracks();
        if (audioTracks.length === 0) {
          throw new Error(
            "Video has no audio track. Please upload a video with audio.",
          );
        }

        // Ensure audio track is enabled
        audioTracks[0].enabled = true;

        const audioOnlyStream = new MediaStream([audioTracks[0]]);

        // Pick best mime type
        const mimeOptions = [
          "audio/webm;codecs=opus",
          "audio/webm",
          "video/webm;codecs=vp8,opus",
        ];
        let selectedMime = "";
        for (const m of mimeOptions) {
          if (MediaRecorder.isTypeSupported(m)) {
            selectedMime = m;
            break;
          }
        }
        if (!selectedMime) {
          throw new Error("No supported audio recording format found.");
        }

        let mediaRecorder: MediaRecorder;
        try {
          mediaRecorder = new MediaRecorder(audioOnlyStream, {
            mimeType: selectedMime,
            ...(selectedMime.startsWith("audio/")
              ? { audioBitsPerSecond: 16000 }
              : {}),
          });
        } catch {
          mediaRecorder = new MediaRecorder(audioOnlyStream);
        }

        const chunks: Blob[] = [];

        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };

        mediaRecorder.onstop = () => {
          const blob = new Blob(chunks, {
            type: mediaRecorder.mimeType || selectedMime,
          });
          cleanup();
          stream.getTracks().forEach((t: MediaStreamTrack) => t.stop());
          onProgress?.("Audio extracted");
          resolve(blob);
        };

        mediaRecorder.onerror = (err) => {
          cleanup();
          reject(new Error("MediaRecorder failed: " + err));
        };

        video.onended = () => {
          if (mediaRecorder.state === "recording") {
            mediaRecorder.stop();
          }
        };

        // Timeout protection
        const timeout = setTimeout(() => {
          if (mediaRecorder.state === "recording") {
            video.pause();
            mediaRecorder.stop();
          }
        }, 300000);

        mediaRecorder.start(100);
      } catch (error) {
        cleanup();
        reject(error);
      }
    };

    video.onerror = () => {
      let msg = "Failed to load video file";
      if (video.error) {
        const messages: Record<number, string> = {
          1: "Video loading was aborted",
          2: "Network error while loading video",
          3: "Video format not supported or file is corrupted",
          4: "Video format not supported by browser",
        };
        msg = messages[video.error.code] || msg;
      }
      cleanup();
      reject(new Error(msg));
    };

    const loadTimeout = setTimeout(() => {
      cleanup();
      reject(new Error("Video loading timeout."));
    }, 30000);

    video.addEventListener("loadedmetadata", () => clearTimeout(loadTimeout), {
      once: true,
    });
  });
}

/**
 * Extract audio from a video file or URL using native browser APIs.
 * Primary: AudioContext.decodeAudioData (instant, reliable).
 * Fallback: captureStream (real-time playback based).
 */
export async function extractAudioFromVideo(
  videoSource: File | string,
  onProgress?: (message: string) => void,
): Promise<Blob> {
  const isFile = videoSource instanceof File;
  const isUrl = typeof videoSource === "string";

  // console.log("Starting audio extraction...");

  // --- Primary approach: AudioContext.decodeAudioData (fast, no playback) ---
  try {
    onProgress?.("Loading video");

    let arrayBuffer: ArrayBuffer;

    if (isFile) {
      // console.log("Reading file:", videoSource.name, videoSource.size, "bytes");
      arrayBuffer = await videoSource.arrayBuffer();
    } else if (isUrl) {
      // console.log("Fetching video URL...");
      const resp = await fetch(videoSource);
      if (!resp.ok) throw new Error(`Fetch failed: ${resp.status}`);
      arrayBuffer = await resp.arrayBuffer();
    } else {
      throw new Error("Invalid video source");
    }

    // console.log("Trying AudioContext.decodeAudioData...");
    const audioBlob = await extractAudioWithAudioContext(
      arrayBuffer,
      onProgress,
    );

    if (audioBlob && audioBlob.size > 0) {
      // console.log(
      //   "AudioContext extraction succeeded:",
      //   audioBlob.size,
      //   "bytes",
      // );
      onProgress?.("Audio extracted");
      return audioBlob;
    }
    throw new Error("Empty audio blob from AudioContext");
  } catch (audioCtxError) {
    console.warn(
      "AudioContext extraction failed, falling back to captureStream:",
      audioCtxError,
    );
  }

  // --- Fallback: captureStream approach ---
  // console.log("Using captureStream fallback...");

  let videoURL: string;
  let shouldRevokeURL = false;

  if (isFile) {
    let videoBlob: File = videoSource;

    if (!videoSource.type || videoSource.type === "application/octet-stream") {
      const ext = videoSource.name.split(".").pop()?.toLowerCase();
      const mimeMap: Record<string, string> = {
        mp4: "video/mp4",
        webm: "video/webm",
        ogg: "video/ogg",
        mov: "video/quicktime",
        avi: "video/x-msvideo",
        mkv: "video/x-matroska",
      };
      const inferredType = mimeMap[ext || ""] || "video/mp4";
      videoBlob = new File([videoSource], videoSource.name, {
        type: inferredType,
      });
    }

    videoURL = URL.createObjectURL(videoBlob);
    shouldRevokeURL = true;
  } else if (isUrl) {
    videoURL = videoSource;
    shouldRevokeURL = false;
  } else {
    throw new Error("Invalid video source: must be File or URL string");
  }

  return extractAudioWithCaptureStream(videoURL, shouldRevokeURL, onProgress);
}

/**
 * Send audio to transcription API and get text back
 * @param audioBlob - The audio blob to transcribe
 * @param language - Language code (e.g., 'en', 'es', 'fr')
 * @param accessToken - JWT access token for authentication
 * @returns Promise<string> - The transcription text
 */
export async function transcribeAudio(
  audioBlob: Blob,
  language: string,
  // accessToken: string,
  onProgress?: (message: string) => void,
): Promise<string> {
  try {
    // console.log("🎙️ Starting transcription...");
    // console.log("Audio blob size:", audioBlob.size, "bytes");
    // console.log("Language:", language);
    // console.log("Access token exists:", !!accessToken);

    onProgress?.("Transcribing audio");

    // Create FormData with audio file and language
    const formData = new FormData();
    // Use appropriate filename based on blob type
    let filename = "audio.mp3";
    if (audioBlob.type.includes("webm")) filename = "audio.webm";
    else if (audioBlob.type.includes("wav")) filename = "audio.wav";
    formData.append("audio", audioBlob, filename);
    formData.append("language", language);

    // Call transcription API
    const response = await axios.post(
      `${import.meta.env.VITE_CHAT_API_URL}/cloud/transcribe`,
      formData,
      {
        headers: {
          // accesstoken: accessToken,
          // Don't set Content-Type, let axios set it with boundary
        },
        withCredentials: true,
        timeout: 120000, // 2 minute timeout
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1),
          );
          // console.log(`Upload progress: ${percentCompleted}%`);
        },
      },
    );

    onProgress?.("Transcription complete");

    // Extract transcription text from response
    // Adjust this based on actual API response structure
    const transcriptionText =
      response?.data?.text ||
      response?.data?.transcription ||
      response?.data?.data?.text ||
      response?.data?.data?.transcription ||
      response?.data?.result ||
      "";

    if (!transcriptionText) {
      console.warn("⚠️ No transcription text in response:", response.data);
      throw new Error("No transcription text in API response");
    }

    return transcriptionText;
  } catch (error) {
    console.error("❌ Transcription error:", error);

    if (axios.isAxiosError(error)) {
      console.error("Axios error details:", {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.response?.headers,
      });

      if (error.response) {
        throw new Error(
          `API error ${error.response.status}: ${JSON.stringify(error.response.data)}`,
        );
      } else if (error.request) {
        console.error("Request was made but no response:", error.request);
        throw new Error(
          "No response from transcription API. Check network/CORS.",
        );
      }
    }
    throw new Error(`Failed to transcribe: ${error.message}`);
  }
}

/**
 * Check if a file is a video file
 * @param file - File to check
 * @returns boolean - True if the file is a video
 */
export function isVideoFile(file: File): boolean {
  return (
    file.type.startsWith("video/") ||
    file.name.match(/\.(mp4|webm|ogg|mov)$/i) !== null
  );
}

/**
 * Process video file or URL: extract audio and transcribe
 * This is a convenience function that combines both operations
 * @param videoSource - The video file or URL to process
 * @param language - Language code for transcription
 * @param accessToken - JWT access token
 * @param onProgress - Progress callback
 * @returns Promise<string> - The transcription text
 */
export async function processVideoTranscription(
  videoSource: File | string,
  language: string,
  // accessToken: string,
  onProgress?: (message: string) => void,
): Promise<string> {
  try {
    // console.log("=== STARTING PROCESS VIDEO TRANSCRIPTION ===");
    // console.log("Step 1: Extracting audio...");

    // Extract audio from video
    const audioBlob = await extractAudioFromVideo(videoSource, onProgress);

    // console.log("=== AUDIO EXTRACTION COMPLETE ===");
    // console.log("Audio blob:", audioBlob);
    // console.log("Audio blob size:", audioBlob.size);
    // console.log("Audio blob type:", audioBlob.type);

    if (!audioBlob || audioBlob.size === 0) {
      throw new Error("Audio extraction produced empty blob");
    }

    // console.log("Step 2: Calling transcription API...");

    // Transcribe the audio
    const transcriptionText = await transcribeAudio(
      audioBlob,
      language,
      // accessToken,
      onProgress,
    );

    // console.log("=== TRANSCRIPTION COMPLETE ===");
    // console.log("Transcription text:", transcriptionText);

    return transcriptionText;
  } catch (error) {
    console.error("=== ERROR IN PROCESS VIDEO TRANSCRIPTION ===");
    console.error("Error:", error);
    console.error("Stack:", error.stack);
    throw error;
  }
}
